import React from 'react';
import { Link } from 'react-router-dom';
import { Tag } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';
import type { TFunction } from 'i18next';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';

type ModalInfo = { orgId: string; action: 'delete' | 'status' } | null;
type SetModalInfo = React.Dispatch<React.SetStateAction<ModalInfo>>;

interface UseOrganizationTableColumnsProps {
  setModalInfo: SetModalInfo;
  onEdit: (org: FindOrganizationDto) => void;
  t: TFunction<'organization'>;
}

export function useOrganizationTableColumns({
  setModalInfo,
  onEdit,
  t,
}: UseOrganizationTableColumnsProps) {
  const columns = React.useMemo(
    () => [
      {
        title: t('organization:table.name'),
        dataIndex: 'name',
        key: 'name',
        render: (_text: string, row: FindOrganizationDto) => (
          <Link to={`/admin/back-office/organizations/${row.id}`}>
            {row.name}
          </Link>
        ),
      },
      {
        title: t('organization:table.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) =>
          value ? new Date(value).toLocaleDateString() : '-',
      },
      {
        title: t('organization:table.type'),
        dataIndex: 'organizationType',
        key: 'organizationType',
        render: (value: string) => (
          <Tag>{t(`dialog.types.${value}`, value.replace(/_/g, ' '))}</Tag>
        ),
      },
      {
        title: t('organization:table.active'),
        dataIndex: 'active',
        key: 'active',
        render: (value: boolean) => (
          <Tag color={value ? 'green' : 'red'}>
            {value ? 'Active' : 'Inactive'}
          </Tag>
        ),
      },
      {
        title: t('organization:table.actions'),
        key: 'actions',
        align: 'center' as const,
        minWidth: 56,
        render: (_: FindOrganizationDto, row: FindOrganizationDto) => (
          <TableRowActionsMenu
            items={[
              {
                key: 'toggle',
                icon: row.active ? <PoweroffOutlined /> : <CloseOutlined />,
                label: row.active
                  ? t('organization:table.deactivate')
                  : t('organization:table.activate'),
                onClick: () =>
                  setModalInfo({ orgId: row.id, action: 'status' }),
              },
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: t('organization:table.edit'),
                onClick: () => onEdit(row),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: t('organization:table.delete'),
                danger: true,
                onClick: () =>
                  setModalInfo({ orgId: row.id, action: 'delete' }),
              },
            ]}
          />
        ),
      },
    ],
    [onEdit, setModalInfo, t],
  );
  return columns;
}
